import React from "react";
import MuiAlert from "@mui/material/Alert";
import clsx from "clsx";

import Error from "@tiny/icons/generated/Error";
import Info from "@tiny/icons/generated/Info";
import CircleCheck from "@tiny/icons/generated/CircleCheck";
import { useStyles } from "./Alert.styles";
import { AlertProps } from "./Alert.types";
import makeStyles from "@mui/styles/makeStyles";

const iconMapping = {
  error: <Error />,
  success: <CircleCheck />,
  info: <Info />,
  warning: <Error />,
};

const makeAlertClasses = makeStyles((theme) => ({
  root: {
    boxSizing: "border-box",
    display: "flex",
    padding: "4px 8px",
    background: theme.palette.grey[100],
    color: theme.palette.grey[900],
    lineHeight: "16px",
    fontSize: 14,
  },
  icon: {
    margin: "0 8px 0 0",
    padding: 0,
    "& > svg": {
      width: 16,
      height: 16,
      fill: "currentColor",
    },
  },
  message: {
    padding: 0,
  },
  standardError: {
    "& svg": {
      color: theme.palette.red.main,
    },
  },
  standardSuccess: {
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  standardWarning: {
    "& svg": {
      color: theme.palette.yellow.main,
    },
  },
  standardInfo: {
    "& svg": {
      color: theme.palette.blue.main,
    },
  },
  filledError: {
    background: theme.palette.red.lighter,
    "& svg": {
      color: theme.palette.red.dark,
    },
  },
  filledSuccess: {
    background: theme.palette.primary.lighter,
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  filledWarning: {
    background: theme.palette.yellow.lighter,
    "& svg": {
      color: theme.palette.yellow.dark,
    },
  },
  filledInfo: {
    background: theme.palette.blue.lighter,
    "& svg": {
      color: theme.palette.blue.dark,
    },
  },
  action: {
    alignItems: "flex-start",
    marginRight: 0,
    paddingLeft: 4,
    padding: 0,
    "& > button": {
      width: 16,
      height: 16,
      "& svg": {
        width: 16,
        height: 16,
        fill: "currentColor",
      },
    },
  },
}));

function Alert(props: AlertProps) {
  const {
    size,
    className: classNameProp,
    noBorderRadius,
    noTopBorderRadius,
    bgColor,
    ...other
  } = props;

  const classes = useStyles();

  const alertClasses = makeAlertClasses();

  const className = clsx(classNameProp, {
    [classes.small]: size === "small",
    [classes.noBorderRadius]: noBorderRadius,
    [classes.noTopBorderRadius]: noTopBorderRadius,
    [classes.bgWhite]: bgColor === "white",
  });

  return (
    <MuiAlert
      {...other}
      className={className}
      classes={alertClasses}
      iconMapping={iconMapping}
    />
  );
}

export default Alert;
